import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { AlertTriangle, XCircle, RefreshCw } from 'lucide-react';

/**
 * DataFreshnessBanner
 * Warn users when a specific data source hasn't refreshed on schedule.
 *
 * Props:
 *   sources: array of source keys to watch, e.g. ["auctions"]
 *            or ["dld_transactions","dld_deep_sync"]
 *   compact: boolean - single-line compact style vs full banner
 *
 * Valid source keys (from GET /api/system/data-freshness):
 *   auctions | dld_transactions | dld_deep_sync
 *   live_listings | propertyfinder | iris_scoring
 */
export default function DataFreshnessBanner({ sources = [], compact = false }) {
  const [state, setState] = useState({ loading: true, items: [] });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/system/data-freshness');
        if (!alive) return;
        const filtered = (data.sources || []).filter(
          (s) => sources.includes(s.key) && s.level !== 'ok'
        );
        setState({ loading: false, items: filtered });
      } catch (e) {
        if (alive) setState({ loading: false, items: [] });
      }
    })();
    return () => { alive = false; };
  }, [sources]);

  if (state.loading || state.items.length === 0) return null;

  const worst = state.items.some((s) => s.level === 'critical') ? 'critical' : 'warning';
  const isCrit = worst === 'critical';

  const wrapCls = isCrit
    ? 'border-red-300 bg-red-50 text-red-900'
    : 'border-amber-300 bg-amber-50 text-amber-900';
  const Icon = isCrit ? XCircle : AlertTriangle;

  if (compact) {
    return (
      <div
        data-testid={`freshness-banner-${worst}`}
        className={`flex items-center gap-2 text-[11px] rounded-full border px-3 py-1 ${wrapCls}`}
        title={state.items.map((s) => s.message).join(' · ')}
      >
        <Icon size={12}/>
        <span className="truncate">
          {state.items[0].label} {state.items[0].hours_since
            ? `is ${state.items[0].hours_since}h stale`
            : 'has never refreshed'}
          {state.items.length > 1 ? ` (+${state.items.length - 1} more)` : ''}
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid={`freshness-banner-${worst}`}
      className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${wrapCls}`}
    >
      <Icon className="shrink-0 mt-0.5" size={18}/>
      <div className="text-sm leading-relaxed flex-1">
        <div className="font-medium mb-1">
          {isCrit ? 'Data may be out of date' : 'Data is a bit stale'}
        </div>
        <ul className="space-y-0.5">
          {state.items.map((s) => (
            <li key={s.key} className="text-xs">• {s.message}</li>
          ))}
        </ul>
      </div>
      <RefreshCw
        size={16}
        className="shrink-0 mt-0.5 opacity-60 cursor-pointer hover:opacity-100"
        title="Reload page to check again"
        onClick={() => window.location.reload()}
        data-testid="freshness-banner-reload"
      />
    </div>
  );
}
